import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GeneratedOpcUaClientCertificate } from "@src/cloud-connect/api/client-configuration/post-generate-opcua-client-certificate";
import {
  AnnouncementStatus,
  GatewayOnboardingStatus,
  MachineAvailabilityStatus,
  MachineDetails,
  MachineOnboardingStatus,
  OnboardingDetails,
  OnboardingState,
} from "@src/types/gateway-onboarding";

export const initialState: OnboardingState = {
  gatewayOnboardingStatus: GatewayOnboardingStatus.GATEWAY_NOT_STARTED,
  announcementStatus: AnnouncementStatus.IDLE,
  onboardingDetails: {
    gateway: {},
  },
  machineOnboardingStatus: MachineOnboardingStatus.MACHINE_NOT_STARTED,
  machineAvailabilityStatus: MachineAvailabilityStatus.IDLE,
  machineAvailabilityError: "",
  machineSkillsLabel: "",
  machineOnboardingSummary: {
    status: "IDLE",
    installedSkills: [],
    failedSkills: [],
    skippedSkills: [],
  },
  connectDataSourceClientCertificate: null,
};

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    setGatewayOnboardingStatus(
      state,
      action: PayloadAction<GatewayOnboardingStatus>,
    ) {
      state.gatewayOnboardingStatus = action.payload;
    },

    setAnnouncementStatus(state, action: PayloadAction<AnnouncementStatus>) {
      state.announcementStatus = action.payload;
    },

    setOnboardingDetails(state, action: PayloadAction<OnboardingDetails>) {
      state.onboardingDetails = action.payload;
    },

    setGatewayDetails(
      state,
      action: PayloadAction<OnboardingDetails["gateway"]>,
    ) {
      state.onboardingDetails.gateway = action.payload;
    },

    setMachineOnboardingStatus(
      state,
      action: PayloadAction<MachineOnboardingStatus>,
    ) {
      state.machineOnboardingStatus = action.payload;
    },

    setMachineAvailabilityStatus(
      state,
      action: PayloadAction<MachineAvailabilityStatus>,
    ) {
      state.machineAvailabilityStatus = action.payload;
    },

    setMachineAvailabilityError(state, action: PayloadAction<string>) {
      state.machineAvailabilityError = action.payload;
    },

    setMachineSkillsLabel(state, action: PayloadAction<string>) {
      state.machineSkillsLabel = action.payload;
    },

    setMachineOnboardingSummary(
      state,
      action: PayloadAction<OnboardingState["machineOnboardingSummary"]>,
    ) {
      state.machineOnboardingSummary = action.payload;
    },

    setMachineDetails(state, action: PayloadAction<MachineDetails>) {
      state.onboardingDetails.machine = action.payload;
    },

    setConnectDataSourceClientCertificate(
      state,
      action: PayloadAction<GeneratedOpcUaClientCertificate | null>,
    ) {
      state.connectDataSourceClientCertificate = action.payload;
    },

    resetOnboardingState() {
      return initialState;
    },
  },
});

export const {
  setGatewayOnboardingStatus,
  setAnnouncementStatus,
  setOnboardingDetails,
  setGatewayDetails,
  setMachineOnboardingStatus,
  setMachineAvailabilityStatus,
  setMachineAvailabilityError,
  setMachineSkillsLabel,
  setMachineOnboardingSummary,
  setMachineDetails,
  setConnectDataSourceClientCertificate,
  resetOnboardingState,
} = onboardingSlice.actions;

export default onboardingSlice.reducer;
